import React, { useEffect, useState } from "react";
import { Row, Col, Card, ProgressBar } from "react-bootstrap";
import { FormattedMessage, useIntl } from "react-intl";
import { getIndicatorRegionalDelegationGroupsCounter } from "../../../../../rest/indicator";
import Loading from "../../../../../components/general/Loading";
import LastUpdate from "../utils/LastUpdate";
import {
  handleError,
  isNotBusinessError,
} from "../../../../../utils/handleError";
import ExportDialog from "../utils/ExportDialog";
import * as XLSX from "xlsx";
import { buildWorkbookDelegation } from "../sectionsIndicatorsCandidatures/exporters/exporterIndicatorDelegation";
import KpiStat from "../utils/KpiStat";
import { useClientPdf } from "../utils/useClientPdf";

export default function IndicatorDelegation() {
  const intl = useIntl();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState([]);
  const [showExport, setShowExport] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [refreshVersion, setRefreshVersion] = useState(null);

  const { pdfRef, exportPdf } = useClientPdf({
    marginMm: 10,
    orientation: "portrait",
    hideSelectors: ['[data-hide-on-export="true"]', ".modal", ".toast"],
  });

  useEffect(() => {
    const fetchDelegationData = async () => {
      try {
        const resp = await getIndicatorRegionalDelegationGroupsCounter();
        const payload = resp?.data || {};
        setData(payload.data || []);
        setLastUpdate(payload.lastRefreshedAt || null);
        setRefreshVersion(payload.refreshVersion || null);
      } catch (err) {
        if (isNotBusinessError(err)) setError(err);
      } finally {
        setLoading(false);
      }
    };
    fetchDelegationData();
  }, []);

  if (loading) return <Loading />;
  if (error) return handleError(error);

  const sumField = (field) => data.reduce((s, i) => s + (i?.[field] || 0), 0);
  const totalAll = sumField("counter");
  const toPct = (v, t) => (t > 0 ? Math.round((v / t) * 100) : 0);

  const STATE_DEFS = [
    {
      key: "sumSubmitted",
      id: "groupState.0",
      label: "Submetidas",
      color: "#0d6efd",
    },
    {
      key: "sumDeletedNoValidation",
      id: "groupState.1",
      label: "Anuladas",
      color: "#6f42c1",
    },
    {
      key: "sumEligibility",
      id: "groupState.2",
      label: "Por tratar",
      color: "#6c757d",
    },
    {
      key: "sumApproved",
      id: "groupState.3",
      label: "Aprovadas",
      color: "#198754",
    },
    {
      key: "sumRevocation",
      id: "groupState.4",
      label: "Revogação",
      color: "#ffc107",
    },
    {
      key: "sumRejected",
      id: "groupState.5",
      label: "Indeferidas",
      color: "#dc3545",
    },
    {
      key: "sumDeleted",
      id: "groupState.6",
      label: "Desistência",
      color: "#212529",
    },
    {
      key: "sumOthers",
      id: "groupState.7",
      label: "Em tratamento/Outros",
      color: "#0dcaf0",
    },
  ];

  const totalsByState = STATE_DEFS.map((s) => {
    const value = sumField(s.key);
    return { ...s, value, pct: toPct(value, totalAll) };
  });

  const sortedByTotal = data.slice().sort((a, b) => b.counter - a.counter);

  const handleExportExcelFinal = async () => {
    try {
      const wb = await buildWorkbookDelegation();
      XLSX.writeFile(wb, "indicadores_delegacao.xlsx");
      setShowExport(false);
    } catch (e) {
      console.error("[Excel Delegacao] falhou:", e);
      alert("Falha ao gerar Excel: " + (e?.message || e));
    }
  };

  const handleExportPdfClient = async () => {
    try {
      await exportPdf("indicadores_delegacao.pdf");
      setShowExport(false);
    } catch (e) {
      console.error("[PDF Delegacao] falhou:", e);
      alert("Falha ao gerar PDF: " + (e?.message || e));
    }
  };

  const DelegationStackedRow = ({ item }) => {
    const total = item?.counter || 0;
    return (
      <div className="mb-3">
        <div className="d-flex justify-content-between">
          <div className="text-truncate">
            <FormattedMessage
              id={`regionalDelegation.${item.code}`}
              defaultMessage={item.code}
            />
          </div>
          <div className="ms-2">
            <strong>{total}</strong>
          </div>
        </div>

        <ProgressBar style={{ height: 10 }}>
          {STATE_DEFS.map((s) => {
            const val = item?.[s.key] || 0;
            if (val <= 0) return null;
            const pct = toPct(val, total);
            const label = intl.formatMessage({
              id: s.id,
              defaultMessage: s.label,
            });
            return (
              <ProgressBar
                key={s.key}
                now={pct}
                style={{ backgroundColor: s.color }}
                title={`${label}: ${val} (${pct}%)`}
              />
            );
          })}
        </ProgressBar>
      </div>
    );
  };

  return (
    <div className="container-fluid" ref={pdfRef}>
      <Row className="align-items-center mb-3">
        <Col>
          <h4 className="text-secondary mb-0">
            <FormattedMessage
              id="indicator.candidatures.byRegionalDelegation"
              defaultMessage="Candidaturas por Delegação Regional"
            />
          </h4>
          <small className="text-muted d-block">
            Este indicador contabiliza candidaturas.
          </small>
          <small className="text-muted">
            O total pode ser inferior ao total global da Medida quando existem candidaturas sem localização preenchida.
          </small>
        </Col>
        <Col className="text-end">
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              setShowExport(true);
            }}
            style={{ color: "#e5533d", textDecoration: "underline" }}
            data-hide-on-export="true"
          >
            <FormattedMessage
              id="indicator.exportar"
              defaultMessage="Exportar"
            />
          </a>
        </Col>
      </Row>

      <Row className="g-3 align-items-stretch mb-3">
        <Col xs={6} md={3} lg={2}>
          <KpiStat
            label={intl.formatMessage({
              id: "indicator.total",
              defaultMessage: "Total",
            })}
            value={totalAll}
          />
        </Col>
        <Col xs={6} md={3} lg={2}>
          <KpiStat
            label={intl.formatMessage({
              id: "indicator.delegations.distinct",
              defaultMessage: "Delegações",
            })}
            value={data.length}
          />
        </Col>

        <Col xs={12} md={6} lg={8}>
          <Card className="shadow-sm h-100">
            <Card.Body className="py-3">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="text-secondary mb-0">
                  <FormattedMessage
                    id="indicator.status.distribution"
                    defaultMessage="Distribuição por Estado (global)"
                  />
                </h6>
                <div className="text-muted">
                  <FormattedMessage
                    id="indicator.total"
                    defaultMessage="Total"
                  />
                  : <strong>{totalAll}</strong>
                </div>
              </div>

              <ProgressBar style={{ height: 12 }}>
                {totalsByState
                  .filter((s) => s.value > 0)
                  .map((s) => {
                    const label = intl.formatMessage({
                      id: s.id,
                      defaultMessage: s.label,
                    });
                    return (
                      <ProgressBar
                        key={s.key}
                        now={s.pct}
                        style={{ backgroundColor: s.color }}
                        title={`${label}: ${s.value} (${s.pct}%)`}
                      />
                    );
                  })}
              </ProgressBar>

              <div className="d-flex flex-wrap gap-2 mt-2">
                {totalsByState.map((s) => (
                  <div key={s.key} className="d-flex align-items-center me-2">
                    <span
                      className="d-inline-block rounded me-1"
                      style={{
                        width: 10,
                        height: 10,
                        backgroundColor: s.color,
                      }}
                    />
                    <span className="text-nowrap">
                      <FormattedMessage id={s.id} defaultMessage={s.label} />:{" "}
                      {s.value} ({s.pct}%)
                    </span>
                  </div>
                ))}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card className="mb-3 shadow-sm">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="text-secondary mb-0">
              <FormattedMessage
                id="indicator.byDelegation.distribution"
                defaultMessage="Distribuição por Delegação"
              />
            </h6>
            <div className="text-muted">
              <FormattedMessage id="indicator.total" defaultMessage="Total" />:{" "}
              <strong>{totalAll}</strong>
            </div>
          </div>

          {sortedByTotal.length === 0 && (
            <div className="text-muted">Sem dados</div>
          )}

          {sortedByTotal.map((item) => (
            <DelegationStackedRow key={item.code} item={item} />
          ))}
        </Card.Body>
      </Card>

      <LastUpdate iso={lastUpdate} version={refreshVersion} className="mt-3" />

      <ExportDialog
        show={showExport}
        onHide={() => setShowExport(false)}
        options={[
          {
            onClick: handleExportExcelFinal,
            filename: "indicadores_delegacao.xlsx",
          },
          {
            onClick: () => exportPdf("indicadores_delegacao.pdf"),
            filename: "indicadores_delegacao.pdf",
          },
        ]}
      />
    </div>
  );
}